import { useEffect, useRef, useState, type ReactNode } from 'react';
import { SkuField } from '@/features/shopify-prefill';
import type { Direction, Product } from '@/entities/label';
import { Field, Fold, Toggle } from '@/shared/ui';

// A text box whose typed text lives only in its value, as the original pages' did (React would also copy it into the page's
// text, where it would show up twice for anything that reads the page). Changes from outside (another tab, Fill from Website)
// are written into it.
function TextArea({ id, value, onChange, placeholder, maxLength }: { id: string; value: string; onChange: (v: string) => void; placeholder: string; maxLength?: number }) {
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (box.current && box.current.value !== value) box.current.value = value; }, [value]);
  return <textarea id={id} ref={box} placeholder={placeholder} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} />;
}

// A labelled text field (label above, 48px box).
function TextField({ label, value, onChange, placeholder, multiline, maxLength }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; multiline?: boolean; maxLength?: number }) {
  return (
    <Field label={label}>
      {(id) => multiline
        ? <TextArea id={id} placeholder={placeholder} maxLength={maxLength} value={value} onChange={onChange} />
        : <input id={id} type="text" placeholder={placeholder} maxLength={maxLength} value={value} onChange={(e) => onChange(e.target.value)} />}
    </Field>
  );
}

// The small triangle inside the Item Location buttons
function DirIcon({ dir }: { dir: Direction }) {
  const rot = ({ up: 0, right: 90, down: 180, left: 270 } as Record<string, number>)[dir] || 0;
  return (
    <svg width="20" height="20" viewBox="0 0 100 100" style={{ transform: `rotate(${rot}deg)` }} aria-hidden="true">
      <polygon className="dir-tri" points="50,18 86,78 14,78" stroke="currentColor" strokeWidth="14" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function DirButton({ dir, title, current, onPick, children }: { dir: Direction; title: string; current: Direction; onPick: (d: Direction) => void; children: ReactNode }) {
  return (
    <button type="button" className="dir-btn" title={title} aria-label={dir === 'none' ? undefined : title} aria-pressed={current === dir} onClick={() => onPick(dir)}>{children}</button>
  );
}

const DIR_WORD: Record<Direction, string> = { none: 'Off', up: 'Up', down: 'Down', left: 'Left', right: 'Right' };

interface Props {
  tab: number;
  count: number;
  product: Product;
  limits: { name?: number; desc?: number };
  set: (patch: Partial<Product>) => void;
  readProduct: (idx: number) => Product;
  fill: (idx: number, patch: Partial<Product>) => void;
  copyFirst: () => void;
  tabs: ReactNode;
}

// One label's fields, as folding groups of the side panel: the label itself (tabs, SKU with Fill from Website, name,
// description, prices), Extra info, Item location and QR code. Every group starts open; a closed one stays closed in this browser.
export function ProductForm({ tab, count, product: p, limits, set, readProduct, fill, copyFirst, tabs }: Props) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (!copied) return; const t = setTimeout(() => setCopied(false), 1500); return () => clearTimeout(t); }, [copied]);
  const pick = (direction: Direction) => set({ direction });
  return (
    <>
      <Fold title="Product Data" note={count > 1 ? `Label ${tab + 1} of ${count}` : undefined} open remember="labels.product">
        {tabs}
        <SkuField tab={tab} product={p} onSku={(sku) => set({ sku })} readProduct={readProduct} fill={fill} />
        <TextField label="Product Name" placeholder="e.g. Organic Green Tea" maxLength={limits.name} value={p.name} onChange={(name) => set({ name })} />
        <TextField label="Description" placeholder="e.g. Premium loose leaf, 100g" multiline maxLength={limits.desc} value={p.desc} onChange={(desc) => set({ desc })} />
        <div className="grid2">
          <TextField label="Price" placeholder="$12.99" value={p.price} onChange={(price) => set({ price })} />
          <TextField label="Compare At" placeholder="$19.99" value={p.compare} onChange={(compare) => set({ compare })} />
        </div>
        {tab === 0 && count > 1 && (
          <button type="button" className="btn ghost" style={{ width: '100%' }} onClick={() => { copyFirst(); setCopied(true); }}>
            {copied ? '✓ Copied!' : '↓ Copy Label 1 to All Labels'}
          </button>
        )}
      </Fold>
      <Fold title="Extra Info" note={[p.ex1k, p.ex2k].filter(Boolean).join(', ') || 'None'} open remember="labels.extra">
        <div className="grid2">
          <TextField label="Label A" placeholder="Weight" value={p.ex1k} onChange={(ex1k) => set({ ex1k })} />
          <TextField label="Value A" placeholder="100g" value={p.ex1v} onChange={(ex1v) => set({ ex1v })} />
        </div>
        <div className="grid2">
          <TextField label="Label B" placeholder="Origin" value={p.ex2k} onChange={(ex2k) => set({ ex2k })} />
          <TextField label="Value B" placeholder="Japan" value={p.ex2v} onChange={(ex2v) => set({ ex2v })} />
        </div>
      </Fold>
      <Fold title="Item Location" note={DIR_WORD[p.direction]} open remember="labels.location">
        <p className="hint" style={{ marginTop: 0, marginBottom: 12 }}>Select the item's position on the rack or shelf. Leave OFF if the label goes directly on the item.</p>
        <div className="dir-pad" role="group" aria-label="Item Location">
          <span />
          <DirButton dir="up" title="Up" current={p.direction} onPick={pick}><DirIcon dir="up" /></DirButton>
          <span />
          <DirButton dir="left" title="Left" current={p.direction} onPick={pick}><DirIcon dir="left" /></DirButton>
          <DirButton dir="none" title="No arrow" current={p.direction} onPick={pick}>OFF</DirButton>
          <DirButton dir="right" title="Right" current={p.direction} onPick={pick}><DirIcon dir="right" /></DirButton>
          <span />
          <DirButton dir="down" title="Down" current={p.direction} onPick={pick}><DirIcon dir="down" /></DirButton>
          <span />
        </div>
      </Fold>
      <Fold title="QR Code" note={p.showQR ? 'On' : 'Off'} open remember="labels.qr">
        <Toggle label="Show QR Code" checked={p.showQR} onChange={(showQR) => set({ showQR })} />
        <TextField label="QR Content (URL or text)" placeholder="https://yourstore.com/product/abc-001" value={p.qrText} onChange={(qrText) => set({ qrText })} />
      </Fold>
    </>
  );
}
